import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Avatar,
} from '@mui/material';
import AppLayout from '../../components/AppLayout';
import DateFilterDialog from '../../components/DateFilterDialog';
import ProductListDialog from '../../components/ProductListDialog';
import API_BASE_URL from '../../config/api';
import { toast } from 'react-toastify';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import GroupIcon from '@mui/icons-material/Group';

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [productListOpen, setProductListOpen] = useState(false);
  const [selectedProductList, setSelectedProductList] = useState({ title: '', products: [], type: '' });
  // Helper function to format date without timezone issues
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const range = {
      startDate: formatDateLocal(firstDay),
      endDate: formatDateLocal(lastDay),
    };
    
    return range;
  });
  const [dateLabel, setDateLabel] = useState('This Month');



  useEffect(() => {
    fetchAnalytics(dateRange);
  }, []);

  // Add dashboard refresh listener
  useEffect(() => {
    const handleDashboardRefresh = () => {
      // Add a small delay to ensure database operations are complete
      setTimeout(() => {
        fetchAnalytics(dateRange);
      }, 200);
    };

    // Listen for custom events to refresh dashboard data
    window.addEventListener('refreshDashboardData', handleDashboardRefresh);
    
    // Also listen for storage events in case of cross-tab updates
    window.addEventListener('storage', (e) => {
      if (e.key === 'refreshDashboardData') {
        handleDashboardRefresh();
      }
    });
    
    return () => {
      window.removeEventListener('refreshDashboardData', handleDashboardRefresh);
      window.removeEventListener('storage', handleDashboardRefresh);
    };
  }, [dateRange]);

  const fetchAnalytics = async (range) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/dashboard/analytics?startDate=${range.startDate}&endDate=${range.endDate}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        toast.error('Failed to fetch dashboard analytics');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };



  const handleDateFilterApply = (range) => {
    setDateRange(range);
    
    // Set label based on range
    const today = formatDateLocal(new Date());
    const thisMonthStart = formatDateLocal(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const thisMonthEnd = formatDateLocal(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
    const thisYearStart = formatDateLocal(new Date(new Date().getFullYear(), 0, 1));
    const thisYearEnd = formatDateLocal(new Date(new Date().getFullYear(), 11, 31));

    if (range.startDate === today && range.endDate === today) {
      setDateLabel('Today');
    } else if (range.startDate === thisMonthStart && range.endDate === thisMonthEnd) {
      setDateLabel('This Month');
    } else if (range.startDate === thisYearStart && range.endDate === thisYearEnd) {
      setDateLabel('This Year');
    } else {
      // Check if it's a month-based selection
      const startDate = new Date(range.startDate);
      const endDate = new Date(range.endDate);
      
      // Check if start date is first day of month
      if (startDate.getDate() === 1) {
        const startMonth = startDate.getMonth();
        const startYear = startDate.getFullYear();
        const endMonth = endDate.getMonth();
        const endYear = endDate.getFullYear();
        
        // Check if end date is last day of a month
        const expectedEndDate = new Date(endYear, endMonth + 1, 0);
        
        if (endDate.getTime() === expectedEndDate.getTime()) {
          // It's a month-based selection
          if (startYear === endYear && startMonth === endMonth) {
            // Single month
            const monthName = startDate.toLocaleString('default', { month: 'long', year: 'numeric' });
            setDateLabel(monthName);
          } else {
            // Month range
            const startMonthName = startDate.toLocaleString('default', { month: 'short', year: 'numeric' });
            const endMonthName = endDate.toLocaleString('default', { month: 'short', year: 'numeric' });
            setDateLabel(`${startMonthName} - ${endMonthName}`);
          }
        } else {
          setDateLabel(`${range.startDate} to ${range.endDate}`);
        }
      } else {
        setDateLabel(`${range.startDate} to ${range.endDate}`);
      }
    }

    fetchAnalytics(range);
  };

  const handleShowProductList = (title, products, type) => {
    setSelectedProductList({ title, products, type });
    setProductListOpen(true);
  };

  const getMoreSoldCategory = () => {
    if (!analytics?.categorySales || analytics.categorySales.length === 0) {
      return { category: 'N/A', quantity: 0 };
    }

    const readyMade = analytics.categorySales.find(c => c.Category === 'Ready Made');
    const customized = analytics.categorySales.find(c => c.Category === 'Customized');

    const readyMadeQty = readyMade?.TotalQuantity || 0;
    const customizedQty = customized?.TotalQuantity || 0;

    if (readyMadeQty > customizedQty) {
      return { category: 'Ready Made', quantity: readyMadeQty };
    } else if (customizedQty > readyMadeQty) {
      return { category: 'Customized', quantity: customizedQty };
    } else {
      return { category: 'Equal', quantity: readyMadeQty };
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: '#C4A574' }} />
        </Box>
      </AppLayout>
    );
  }

  const moreSold = getMoreSoldCategory();

  const StatCard = ({ bg, icon, title, value, sub, onClick }) => (
    <Paper
      elevation={8}
      onClick={onClick}
      sx={{
        p: { xs: 1.5, sm: 2, md: 2 },
        height: { xs: 100, sm: 120, md: 140, lg: 160, xl: 180 },
        borderRadius: 3,
        backgroundColor: bg,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        color: '#000',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.8rem', lg: '0.9rem', xl: '1rem' },
            letterSpacing: 1,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          fontSize: { xs: '0.75rem', sm: '0.8rem', md: '1rem', lg: '1.2rem', xl: '1.3rem' },
          mb: 0.5,
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography
          variant="caption"
          sx={{
            opacity: 0.9,
            fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.7rem', lg: '0.8rem', xl: '0.85rem' },
          }}
        >
          {sub}
        </Typography>
      )}
    </Paper>
  );

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 1, sm: 2, md: 4 }, minHeight: 'calc(100vh - 80px)' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mb: 4,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              letterSpacing: { xs: 1, sm: 2, md: 3 },
              color: '#8B7355',
              textDecoration: 'underline',
              textUnderlineOffset: { xs: 8, sm: 10, md: 12 },
              textDecorationThickness: 2,
              fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.75rem' },
            }}
          >
            DASHBOARD
          </Typography>

          <Button
            variant="contained"
            onClick={() => setDateFilterOpen(true)}
            sx={{
              bgcolor: '#C4A574',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.7rem',
              px: 3,
              py: 1,
              textTransform: 'uppercase',
              '&:hover': { bgcolor: '#B39565' },
            }}
          >
            SORT BY DATE ▾
          </Button>
        </Box>

        {/* Stat Cards Grid */}
        <Grid
          container
          spacing={3}
          sx={{
            maxWidth: 1400,
            mx: 'auto',
            justifyContent: 'center',
          }}
        >
          {/* Top Selling */}
          <Grid
            item
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexBasis: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
              maxWidth: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
            }}
          >
            <StatCard
              bg="#F5D4BE"
              icon={<TrendingUpIcon sx={{ color: '#4CAF50' }} />}
              title="TOP SELLING"
              value={analytics?.topSelling?.length || 0}
              sub="Click to view top 20 products"
              onClick={() =>
                handleShowProductList('Top Selling Products (Top 20)', analytics?.topSelling || [], 'top')
              }
            />
          </Grid>

          {/* Low Selling */}
          <Grid
            item
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexBasis: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
              maxWidth: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
            }}
          >
            <StatCard
              bg="#D9F0EC"
              icon={<TrendingDownIcon sx={{ color: '#f44336' }} />}
              title="LOW SELLING"
              value={analytics?.lowSelling?.length || 0}
              sub="Click to view bottom 20 products"
              onClick={() =>
                handleShowProductList('Low Selling Products (Bottom 20)', analytics?.lowSelling || [], 'low')
              }
            />
          </Grid>

          {/* Best Performer Sales */}
          <Grid
            item
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexBasis: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
              maxWidth: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
            }}
          >
            <Paper
              elevation={8}
              sx={{
                p: 3,
                height: { xs: 140, sm: 160, md: 180 },
                borderRadius: 3,
                backgroundColor: '#EDE0C9',
                color: '#000',
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
                },
              }}
            >
              <Box sx={{ position: 'relative', mr: 2 }}>
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 24,
                    height: 24,
                    fill: '#FFD700',
                    zIndex: 2
                  }}
                >
                  <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5m14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
                </Box>
                <Avatar
                  src={analytics?.bestSalesPerformer?.Photo ? `${API_BASE_URL}${analytics.bestSalesPerformer.Photo}` : undefined}
                  sx={{
                    width: 70,
                    height: 70,
                    border: '3px solid #FFD700',
                    bgcolor: '#FF9800',
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: 700
                  }}
                >
                  {analytics?.bestSalesPerformer?.FullName ? analytics.bestSalesPerformer.FullName.charAt(0).toUpperCase() : '?'}
                </Avatar>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '0.9rem', sm: '0.9rem', md: '0.8rem', lg: '0.9rem', xl: '1rem' }, letterSpacing: 1 }}>
                  BEST PERFORMER (Sales)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1rem', md: '0.9rem', lg: '1rem', xl: '1rem' }, mb: 0.5 }}>
                  {analytics?.bestSalesPerformer?.FullName || 'N/A'}
                </Typography>
                {analytics?.bestSalesPerformer && (
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.75rem', md: '0.7rem', lg: '0.8rem', xl: '0.85rem' } }}>
                    Revenue: ₹{parseFloat(analytics.bestSalesPerformer.TotalRevenue).toLocaleString()}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Best Performer Customizer */}
          <Grid
            item
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexBasis: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
              maxWidth: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
            }}
          >
            <Paper
              elevation={8}
              sx={{
                p: 3,
                height: { xs: 140, sm: 160, md: 180 },
                borderRadius: 3,
                backgroundColor: '#D6EAD8',
                color: '#000',
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
                },
              }}
            >
              <Box sx={{ position: 'relative', mr: 2 }}>
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 24,
                    height: 24,
                    fill: '#FFD700',
                    zIndex: 2
                  }}
                >
                  <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5m14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
                </Box>
                <Avatar
                  src={analytics?.bestCustomizer?.Photo ? `${API_BASE_URL}${analytics.bestCustomizer.Photo}` : undefined}
                  sx={{
                    width: 70,
                    height: 70,
                    border: '3px solid #FFD700',
                    bgcolor: '#2196F3',
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: 700
                  }}
                >
                  {analytics?.bestCustomizer?.FullName ? analytics.bestCustomizer.FullName.charAt(0).toUpperCase() : '?'}
                </Avatar>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '0.9rem', sm: '0.9rem', md: '0.8rem', lg: '0.9rem', xl: '1rem' }, letterSpacing: 1 }}>
                  BEST PERFORMER (Customizer)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1rem', md: '0.9rem', lg: '1rem', xl: '1rem' }, mb: 0.5 }}>
                  {analytics?.bestCustomizer?.FullName || 'N/A'}
                </Typography>
                {analytics?.bestCustomizer && (
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.75rem', md: '0.7rem', lg: '0.8rem', xl: '0.85rem' } }}>
                    Tasks: {analytics.bestCustomizer.CompletedTasks}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Best Customer */}
          <Grid
            item
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexBasis: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
              maxWidth: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
            }}
          >
            <StatCard
              bg="#E3F2FD"
              icon={<PersonIcon sx={{ color: '#1976D2' }} />}
              title="BEST CUSTOMER"
              value={analytics?.bestCustomer?.CustomerName || 'N/A'}
              sub={`Items: ${analytics?.bestCustomer?.TotalItems || 0} • Revenue: ₹${parseFloat(analytics?.bestCustomer?.TotalRevenue || 0).toLocaleString()}`}
            />
          </Grid>

          {/* Best Party */}
          <Grid
            item
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexBasis: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
              maxWidth: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
            }}
          >
            <StatCard
              bg="#F0DFF3"
              icon={<GroupIcon sx={{ color: '#9C27B0' }} />}
              title="BEST PARTY"
              value={analytics?.bestParty?.SupplierName || 'N/A'}
              sub={`Quantity: ${analytics?.bestParty?.TotalQuantity || 0} • Revenue: ₹${parseFloat(analytics?.bestParty?.TotalRevenue || 0).toLocaleString()}`}
            />
          </Grid>

          {/* Total Active Schemes */}
          <Grid
            item
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexBasis: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
              maxWidth: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
            }}
          >
            <StatCard
              bg="#FFE5B4"
              icon={<AccountBalanceIcon sx={{ color: '#FF5722' }} />}
              title="TOTAL ACTIVE SCHEMES"
              value={analytics?.totalActiveSchemes || 0}
              sub="Currently active customer schemes"
            />
          </Grid>

          {/* Customer Due */}
          <Grid
            item
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexBasis: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
              maxWidth: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
            }}
          >
            <StatCard
              bg="#FFE0E0"
              icon={<AccountBalanceIcon sx={{ color: '#d32f2f' }} />}
              title="CUSTOMER DUE"
              value={`₹${parseFloat(analytics?.customerDue?.amount || 0).toLocaleString()}`}
              sub={`${analytics?.customerDue?.count || 0} customers owe us`}
            />
          </Grid>

          {/* Customer Credit */}
          <Grid
            item
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexBasis: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
              maxWidth: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
            }}
          >
            <StatCard
              bg="#E8F5E9"
              icon={<AccountBalanceIcon sx={{ color: '#388E3C' }} />}
              title="CUSTOMER CREDIT"
              value={`₹${parseFloat(analytics?.customerCredit?.amount || 0).toLocaleString()}`}
              sub={`${analytics?.customerCredit?.count || 0} customers we owe`}
            />
          </Grid>

          {/* Total Inventory Items */}
          <Grid
            item
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexBasis: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
              maxWidth: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
            }}
          >
            <StatCard
              bg="#E0F2F7"
              icon={<InventoryIcon sx={{ color: '#00796B' }} />}
              title="TOTAL INVENTORY ITEMS"
              value={analytics?.inventoryCount?.total || 0}
              sub={`Ready-Made: ${analytics?.inventoryCount?.readyMade || 0} • Customized: ${analytics?.inventoryCount?.customized || 0}`}
            />
          </Grid>

          {/* More Sold Category */}
          <Grid
            item
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexBasis: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
              maxWidth: { xs: '100%', sm: '50%', md: '50%', lg: '32%', xl: '25%' },
            }}
          >
            <StatCard
              bg="#FFF9E6"
              icon={<CategoryIcon sx={{ color: '#F57C00' }} />}
              title="MORE SOLD CATEGORY"
              value={moreSold.category}
              sub={`Total Quantity: ${moreSold.quantity}`}
            />
          </Grid>
        </Grid>

        {/* Bottom Gradient */}
        <Box
          sx={{
            mt: 6,
            height: 60,
            background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.2))',
            borderRadius: 2,
          }}
        />
      </Box>

      <DateFilterDialog
        open={dateFilterOpen}
        onClose={() => setDateFilterOpen(false)}
        onApply={handleDateFilterApply}
      />

      <ProductListDialog
        open={productListOpen}
        onClose={() => setProductListOpen(false)}
        title={selectedProductList.title}
        products={selectedProductList.products}
        type={selectedProductList.type}
      />
    </AppLayout>
  );
}
